<template>
    <Card
        class="OverviewMetricCard"
        :class="[cardClass, { 'is-loading': loading, 'is-small': size === 'small' }]"
        :size="size === 'small' ? 'small' : 'medium'"
    >
        <div v-if="loading" class="metric-content-skeleton">
            <div class="skeleton-icon"></div>
            <div class="skeleton-details">
                <div class="skeleton-title"></div>
                <div class="skeleton-value"></div>
            </div>
        </div>

        <div v-else class="metric-content">
            <div class="metric-icon" :class="iconClass">
                <i :class="icon"></i>
            </div>
            <div class="metric-details">
                <div class="title-value-row">
                    <h3>{{ title }}</h3>
                    <div class="metric-value">{{ value }}</div>
                </div>
                <div v-if="subText" class="metric-sub-text">
                    {{ subText }}
                </div>
                <div v-if="change" class="metric-change" :class="changeClass">
                    <span>{{ change }}</span>
                    <i
                        class="trend-icon ri-arrow-up-line"
                        :class="{
                            'ri-arrow-down-line': changeType === 'negative',
                        }"
                    ></i>
                </div>
            </div>
        </div>

        <div v-if="!loading && $slots.info" class="info-icon">
            <slot name="info"></slot>
        </div>

        <div v-if="!loading && $slots.actions" class="card-actions">
            <slot name="actions"></slot>
        </div>
        <div v-if="!loading && progress !== null" class="progress-container">
            <div class="progress-bar" :style="{ width: `${progress}%` }"></div>
            <div v-if="progressText" class="progress-text">
                {{ progressText }}
            </div>
        </div>
    </Card>
</template>

<script>
import Card from '@/components/Card';

export default {
    name: 'OverviewMetricCard',
    components: { Card },
    props: {
        title: {
            type: String,
            default: '',
        },
        value: {
            type: [String, Number],
            default: '',
        },
        change: {
            type: String,
            default: null,
        },
        icon: {
            type: String,
            default: 'ri-bar-chart-line',
        },
        theme: {
            type: String,
            default: 'blue',
            validator: (value) =>
                ['blue', 'green', 'yellow', 'red', 'orange', 'purple', 'darkblue'].includes(value),
        },
        changeType: {
            type: String,
            default: 'positive',
            validator: (value) => ['positive', 'negative', 'neutral'].includes(value),
        },
        progress: {
            type: Number,
            default: null,
        },
        progressText: {
            type: String,
            default: null,
        },
        subText: {
            type: String,
            default: null,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        size: {
            type: String,
            default: 'medium',
            validator: (value) => ['medium', 'small'].includes(value),
        },
    },
    computed: {
        cardClass() {
            return `${this.theme.charAt(0).toUpperCase() + this.theme.slice(1)}Card`;
        },
        iconClass() {
            return this.changeType === 'negative'
                ? 'negative'
                : this.changeType === 'positive'
                  ? 'positive'
                  : '';
        },
        changeClass() {
            return this.changeType;
        },
    },
};
</script>

<style lang="scss">
.OverviewMetricCard {
    position: relative;
    overflow: hidden;
    padding: 12px;
    height: 100%;
    display: flex;
    flex-direction: column;

    &.is-loading {
        pointer-events: none;
    }

    &.is-small {
        padding: 8px;

        .metric-icon {
            width: 20px;
            height: 20px;
            font-size: 0.875rem;
        }

        .metric-details {
            h3 {
                font-size: 11px;
            }

            .metric-value {
                font-size: 14px;
            }

            .metric-sub-text {
                font-size: 0.7rem;
            }

            .metric-change {
                font-size: 0.7rem;
            }
        }
    }

    &.DarkblueCard {
        background: var(--color-blue-dark-bg, #161936);
        color: var(--color-text-white, #fff);
        border-color: rgba(255, 255, 255, 0.1);

        .metric-details {
            h3 {
                color: rgba(255, 255, 255, 0.8);
            }

            .metric-value {
                color: var(--color-text-white, #fff);
            }

            .metric-sub-text {
                color: rgba(255, 255, 255, 0.7);
            }
        }

        .metric-icon {
            background-color: rgba(255, 255, 255, 0.1);
            color: var(--color-text-white, #fff);
        }

        .info-icon {
            color: rgba(255, 255, 255, 0.7);

            &:hover {
                color: rgba(255, 255, 255, 1);
            }

            i {
                color: rgba(255, 255, 255, 0.7);
            }
        }
    }

    .metric-content-skeleton {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        position: relative;
        flex: 1;
        padding: 10px;

        .skeleton-icon {
            width: 26px;
            height: 26px;
            border-radius: 50%;
            background: #d6d7e3;
            position: relative;
            overflow: hidden;
            flex-shrink: 0;

            &::after {
                position: absolute;
                top: 0;
                right: 0;
                bottom: 0;
                left: 0;
                transform: translateX(-100%);
                background-image: linear-gradient(
                    90deg,
                    rgba(255, 255, 255, 0.2),
                    rgba(255, 255, 255, 0.6),
                    rgba(255, 255, 255, 0.2)
                );
                animation: shimmer 1.5s infinite ease-out;
                content: '';
            }
        }

        .skeleton-details {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 6px;
            min-width: 0;

            .skeleton-title {
                width: 60%;
                height: 12px;
                border-radius: 3px;
                background: #d6d7e3;
                position: relative;
                overflow: hidden;

                &::after {
                    position: absolute;
                    top: 0;
                    right: 0;
                    bottom: 0;
                    left: 0;
                    transform: translateX(-100%);
                    background-image: linear-gradient(
                        90deg,
                        rgba(255, 255, 255, 0.2),
                        rgba(255, 255, 255, 0.6),
                        rgba(255, 255, 255, 0.2)
                    );
                    animation: shimmer 1.5s infinite ease-out;
                    content: '';
                }
            }

            .skeleton-value {
                width: 80%;
                height: 16px;
                border-radius: 3px;
                background: #d6d7e3;
                position: relative;
                overflow: hidden;

                &::after {
                    position: absolute;
                    top: 0;
                    right: 0;
                    bottom: 0;
                    left: 0;
                    transform: translateX(-100%);
                    background-image: linear-gradient(
                        90deg,
                        rgba(255, 255, 255, 0.2),
                        rgba(255, 255, 255, 0.6),
                        rgba(255, 255, 255, 0.2)
                    );
                    animation: shimmer 1.5s infinite ease-out;
                    content: '';
                }
            }
        }
    }

    @keyframes shimmer {
        100% {
            transform: translateX(100%);
        }
    }

    .metric-content {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        position: relative;
        flex: 1;
    }

    .metric-icon {
        width: 26px;
        height: 26px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1rem;
        background-color: rgba(0, 0, 0, 0.05);
        flex-shrink: 0;

        &.positive {
            background-color: rgba(75, 181, 67, 0.1);
            color: var(--color-green-dark);
        }

        &.negative {
            background-color: rgba(242, 78, 30, 0.1);
            color: var(--color-red);
        }

        &.neutral {
            background-color: rgba(var(--color-yellow-rgb), 0.1);
            color: var(--color-yellow);
        }
    }

    .metric-details {
        flex: 1;
        min-width: 0;

        h3 {
            font-size: 12px;
            margin: 0;
            font-weight: normal;
            color: var(--color-gray);
        }

        .metric-value {
            font-size: 16px;
            font-weight: 600;

            color: var(--color-text-black);
        }

        .metric-change {
            display: flex;
            align-items: center;
            gap: 4px;

            &.positive {
                color: var(--color-green-dark);

                .trend-icon {
                    transform: rotate(0deg);
                }
            }

            &.negative {
                color: var(--color-red);

                .trend-icon {
                    transform: rotate(180deg);
                }
            }

            &.neutral {
                color: var(--color-yellow-dark);

                .trend-icon {
                    display: none;
                }
            }
        }
    }

    .info-icon {
        position: absolute;
        top: 12px;
        right: 12px;
        z-index: 10;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        color: var(--color-text);
        cursor: help;
        opacity: 0.7;
        transition:
            opacity 0.2s ease,
            transform 0.2s ease;
        border-radius: 50%;

        &:hover {
            opacity: 1;
            transform: scale(1.1);
        }

        i {
            font-size: 0.8rem;
        }
    }

    .metric-sub-text {
        font-size: 0.75rem;
        font-weight: 400;
        color: var(--color-text);
        margin-top: 2px;
        line-height: 1.3;
    }

    .metric-change {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 0.75rem;
        font-weight: 400;
        margin-top: 2px;
        line-height: 1.3;

        &.positive {
            color: var(--color-green-dark);

            .trend-icon {
                transform: rotate(0deg);
            }
        }

        &.negative {
            color: var(--color-red);

            .trend-icon {
                transform: rotate(180deg);
            }
        }

        &.neutral {
            color: var(--color-yellow-dark);

            .trend-icon {
                display: none;
            }
        }

        .trend-icon {
            font-size: 0.6rem;
            transition: transform 0.2s ease;
        }
    }

    .card-actions {
        margin-top: 12px;
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
    }

    .progress-container {
        margin-top: 12px;
        height: 6px;
        background-color: rgba(0, 0, 0, 0.05);
        border-radius: 3px;
        position: relative;
        overflow: hidden;

        .progress-bar {
            height: 100%;
            background-color: var(--color-green-dark);
            border-radius: 3px;
            transition: width 0.3s ease;
        }

        .progress-text {
            margin-top: 4px;
            font-size: 12px;
            text-align: right;
            color: var(--color-gray);
        }
    }

    @media (max-width: 768px) {
        .metric-content-skeleton {
            gap: 8px;

            .skeleton-icon {
                width: 24px;
                height: 24px;
            }

            .skeleton-details {
                gap: 4px;

                .skeleton-title {
                    height: 10px;
                }

                .skeleton-value {
                    height: 14px;
                }
            }
        }

        .metric-content {
            gap: 8px;
        }

        .metric-icon {
            width: 24px;
            height: 24px;
            font-size: 1rem;
        }

        .metric-details {
            display: flex;
            flex-direction: column;
            gap: 2px;
            min-width: 0;

            h3 {
                font-size: 0.7rem;
                margin: 0;
                line-height: 1.2;
            }

            .metric-value {
                font-size: 0.85rem;
                font-weight: 600;
                margin: 0;
                color: var(--color-text-black);
            }

            .title-value-row {
                display: flex;
                align-items: center;
                gap: 6px;
                min-width: 0;

                h3 {
                    flex-shrink: 0;
                }

                .metric-value {
                    flex: 1;
                    min-width: 0;
                    text-align: right;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
            }

            .metric-sub-text {
                font-size: 0.65rem;
                margin-top: 1px;
            }

            .metric-change {
                font-size: 0.65rem;
                margin-top: 1px;
                gap: 3px;

                .trend-icon {
                    font-size: 0.6rem;
                }
            }
        }

        .info-icon {
            position: static;
            width: 32px;
            height: 32px;
            margin-left: auto;
            margin-top: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            background-color: rgba(0, 0, 0, 0.05);
            transition:
                background-color 0.2s ease,
                transform 0.2s ease;

            &:hover {
                background-color: rgba(0, 0, 0, 0.1);
                transform: scale(1.1);
            }

            &:active {
                background-color: rgba(0, 0, 0, 0.15);
            }

            i {
                font-size: 0.8rem;
            }
        }

        .card-actions {
            margin-top: 8px;
        }

        .progress-container {
            margin-top: 8px;
            height: 4px;

            .progress-text {
                font-size: 0.65rem;
                margin-top: 2px;
            }
        }
    }
}
</style>
